import { requestSE } from "../request";
import { isTruthyEnv } from "../referrals";
import * as fake from "./fake";
import type {
    SponsoredBillingStatus,
    SponsoredCreateResult,
    SponsoredFailReason,
    SponsoredListResponse,
    SponsoredLookupResult,
    SponsoredMutateResult,
    SponsoredTarget,
} from "./types";

export * from "./types";
export { maskEmail } from "./fake";

const BASE_API_URL =
    import.meta.env["VITE_MEEFOD_API_URL"] ||
    "https://us-central1-meyo-230113.cloudfunctions.net/apiCaloo";

const isFake = () => isTruthyEnv(import.meta.env["VITE_FAKE_SPONSORED_SUBS"]);

/**
 * The backend answers business failures as `{ ok: false, reason }`, sometimes
 * with a 4xx. Normalise both so callers only deal with results.
 */
async function call<T extends { ok: boolean }>(
    run: () => Promise<{ data: T }>,
): Promise<T | { ok: false; reason: SponsoredFailReason }> {
    try {
        return (await run()).data;
    } catch (err: any) {
        const data = err?.response?.data;
        if (data && typeof data.reason === "string") {
            return { ok: false, reason: data.reason, ...data };
        }
        throw err;
    }
}

/** The partner is resolved server-side from the auth token, never from the body. */
export async function getSponsoredSubscriptionsSE(
    uid: string,
): Promise<SponsoredListResponse> {
    if (isFake()) return fake.fakeList(uid);
    const res = await requestSE(uid).get(`${BASE_API_URL}/sponsored/subscriptions`);
    return res.data;
}

export async function getSponsoredBillingStatusSE(
    uid: string,
): Promise<SponsoredBillingStatus> {
    if (isFake()) return fake.fakeBilling(uid);
    const res = await requestSE(uid).get(`${BASE_API_URL}/sponsored/billing`);
    return res.data;
}

/** Paths, not URLs: the backend prefixes an allow-listed origin. */
export async function createSponsoredSetupSessionSE(
    uid: string,
    successPath: string,
    cancelPath: string,
): Promise<{ url: string }> {
    if (isFake()) return fake.fakeSetupSession(uid, successPath);
    const res = await requestSE(uid).post(
        `${BASE_API_URL}/sponsored/billing/setup-session`,
        { successPath, cancelPath },
    );
    return res.data;
}

export async function createSponsoredBillingPortalSE(
    uid: string,
    returnPath: string,
): Promise<{ url: string }> {
    if (isFake()) return { url: returnPath };
    const res = await requestSE(uid).post(
        `${BASE_API_URL}/sponsored/billing/portal`,
        { returnPath },
    );
    return res.data;
}

export async function lookupSponsoredUserSE(
    uid: string,
    target: SponsoredTarget,
): Promise<SponsoredLookupResult> {
    if (isFake()) return fake.fakeLookup(uid, target);
    return (await call(() =>
        requestSE(uid).post(`${BASE_API_URL}/sponsored/lookup`, target),
    )) as SponsoredLookupResult;
}

export async function createSponsoredSubscriptionSE(
    uid: string,
    target: SponsoredTarget,
    idempotencyKey: string,
): Promise<SponsoredCreateResult> {
    if (isFake()) return fake.fakeCreate(uid, target);
    return (await call(() =>
        requestSE(uid).post(`${BASE_API_URL}/sponsored/subscriptions`, target, {
            headers: { "Idempotency-Key": idempotencyKey },
        }),
    )) as SponsoredCreateResult;
}

export async function cancelSponsoredSubscriptionSE(
    uid: string,
    id: string,
): Promise<SponsoredMutateResult> {
    if (isFake()) return fake.fakeCancel(uid, id);
    return (await call(() =>
        requestSE(uid).post(
            `${BASE_API_URL}/sponsored/subscriptions/${encodeURIComponent(id)}/cancel`,
            {}, // body vacío con Content-Type JSON lo rechaza Cloud Functions (400 sin CORS)
        ),
    )) as SponsoredMutateResult;
}

export async function reactivateSponsoredSubscriptionSE(
    uid: string,
    id: string,
): Promise<SponsoredMutateResult> {
    if (isFake()) return fake.fakeReactivate(uid, id);
    return (await call(() =>
        requestSE(uid).post(
            `${BASE_API_URL}/sponsored/subscriptions/${encodeURIComponent(id)}/reactivate`,
            {}, // body vacío con Content-Type JSON lo rechaza Cloud Functions (400 sin CORS)
        ),
    )) as SponsoredMutateResult;
}
